/** Types + constants used by AgentConfigCard.
 *
 * Extracted from AgentConfigCard.tsx to keep that page-component file
 * under the 300-line CC-10 ceiling.
 */

/** Raw agent record from Kaycoo. Schema is owned by the platform; we accept
 *  whatever shape it sends and surface only the fields we know about. */
export interface KaycooAgent {
  id?: string;
  agent_id?: string;
  name?: string;
  description?: string | null;
  provider?: string | null;
  model?: string | null;
  status?: string | null;
  pipeline_role?: string | null;
  // Allow any additional fields the platform sends.
  [key: string]: string | number | boolean | null | undefined;
}

export interface KaycooAgentsResponse {
  agents: KaycooAgent[];
  total: number;
}

export type PipelineRole =
  | 'document_assistant'
  | 'document'
  | 'parties'
  | 'clause'
  | 'gate_1'
  | 'compliance'
  | 'signature'
  | 'gate_2'
  | 'gate'
  | 'orchestrator_bootstrap'
  // Legacy alias for tenants migrating from the old 7-stage flow.
  | 'orchestrator';

/** Stored at clients.settings.agent_assignments.<role> = agent_id */
export type AgentAssignments = Partial<Record<PipelineRole, string>>;

/** Transport selection per role. Stored at clients.settings.agent_transports.<role>.
 *  - 'auto'    (default): try /api/v1/agents/{id}/stream first, fall back to /execute
 *  - 'execute' : skip /stream entirely (HF models that don't stream)
 *  - 'stream'  : /stream only; fail loudly if not streamable (debug / strict) */
export type AgentTransport = 'auto' | 'execute' | 'stream';
export type AgentTransports = Partial<Record<PipelineRole, AgentTransport>>;
export const TRANSPORT_OPTIONS: AgentTransport[] = ['auto', 'execute', 'stream'];

export type TestErrorKind =
  | 'auth_failed'
  | 'agent_not_found'
  | 'agent_inactive'
  | 'rate_limited'
  | 'origin_failure'
  | 'timeout'
  | 'circuit_open'
  | 'kaycoo_error';

export interface TestResult {
  ok: boolean;
  output?: string;
  tokens_used?: number;
  duration_ms?: number;
  error?: string;
  kind?: TestErrorKind;
  cf_ray?: string | null;
  hint?: string;
  error_code?: string;
}

/** Build a rich TestResult from an ApiError thrown by the test endpoint.
 *
 *  The backend returns a JSON body with `detail`, `kind`, `hint`, `cf_ray`,
 *  `error_code`. ApiError parses that body once; we just project the fields
 *  we care about. Falls back gracefully when called with a non-API error. */
export function parseTestError(err: unknown): TestResult {
  if (err && typeof err === 'object' && 'body' in err) {
    const body = (err as { body: Record<string, unknown> | null }).body;
    if (body) {
      return {
        ok: false,
        error: typeof body.detail === 'string' ? body.detail : 'Test failed.',
        kind: typeof body.kind === 'string' ? (body.kind as TestErrorKind) : undefined,
        cf_ray: typeof body.cf_ray === 'string' ? body.cf_ray : null,
        hint: typeof body.hint === 'string' ? body.hint : undefined,
        error_code: typeof body.error_code === 'string' ? body.error_code : undefined,
      };
    }
  }
  return { ok: false, error: err instanceof Error ? err.message : 'Test failed.' };
}

export interface TestAgentResponse {
  agent_id: string;
  output: string;
  tokens_used: number;
  duration_ms: number;
}

export const PIPELINE_ROLES: { key: PipelineRole; label: string; hint: string }[] = [
  { key: 'document_assistant', label: 'CMP Document Assistant', hint: 'Pre-step — Opus 4.7 vision: OCR scanned PDFs / images + recognize handwritten items. Builds the indexed text the Document Agent consumes. Runs only when embedded PDF text extraction is empty.' },
  { key: 'document', label: 'Document Agent', hint: 'Step 1 — extract + structure foundational info from the contract.' },
  { key: 'parties', label: 'Parties Agent', hint: 'Step 2 — deep party identification + roles + signatories.' },
  { key: 'clause', label: 'Clause Agent', hint: 'Step 3 — clause-by-clause CUAD-style classification + risk.' },
  { key: 'gate_1', label: 'Gate 1 — Quality Check', hint: 'Step 4 — gates the first three agents before Compliance.' },
  { key: 'compliance', label: 'Compliance Agent', hint: 'Step 5 — GDPR / data-privacy / jurisdiction review.' },
  { key: 'signature', label: 'Signature Agent', hint: 'Step 6 — execution validity + eIDAS + dates + formalities.' },
  { key: 'gate_2', label: 'Gate 2 — Quality Check', hint: 'Step 7 — gates Compliance + Signature before Final Synthesis.' },
  { key: 'gate', label: 'Gate Agent — Final Synthesis', hint: 'Step 8 — cross-agent consistency + risk + recommendations.' },
  { key: 'orchestrator_bootstrap', label: 'Orchestrator — Final Report', hint: 'Step 9 — senior legal analyst writes the client-facing report.' },
];

export function getId(a: KaycooAgent): string {
  return a.agent_id ?? a.id ?? '';
}

export function getName(a: KaycooAgent): string {
  return a.name ?? a.agent_id ?? a.id ?? 'Unnamed agent';
}

/** Loose match: a Kaycoo agent fits a pipeline role if its `pipeline_role`
 *  tag matches OR if the role keyword appears in the agent name. */
export function matchesRole(agent: KaycooAgent, role: PipelineRole): boolean {
  if (agent.pipeline_role && agent.pipeline_role.toLowerCase() === role) return true;
  const name = (agent.name ?? '').toLowerCase();
  return name.includes(role);
}
